/** Preset labels that can be tagged with `@` in addition to people. */
export const UPDATE_MENTION_TAGS = ['important', 'Followup'];
const MENTION_TOKEN_RE = /@([A-Za-z][A-Za-z0-9.'-]*)/g;
function compactName(first, last) {
    return `${first}${last}`.replace(/[^A-Za-z0-9.'-]/g, '');
}
function isWeakFirstName(first) {
    if (!first || first.length < 2 || first.endsWith('.'))
        return true;
    return /^(mr|mrs|ms|miss|dr|prof)$/i.test(first);
}
/** Handle inserted after `@` — first name when unique, else FirstLast. */
export function mentionHandle(person, people) {
    const first = person.firstName.trim();
    const last = person.lastName.trim();
    if (!first && !last)
        return '';
    if (first && !isWeakFirstName(first)) {
        const sameFirst = people.filter((entry) => entry.firstName.trim().toLowerCase() === first.toLowerCase()).length;
        if (sameFirst <= 1)
            return first;
    }
    const combined = compactName(first, last);
    return combined || first || last;
}
export function extractMentionTags(text) {
    const raw = text ?? '';
    const tags = [];
    const seen = new Set();
    const re = new RegExp(MENTION_TOKEN_RE.source, 'g');
    let match;
    while ((match = re.exec(raw))) {
        const label = match[1];
        const key = label.toLowerCase();
        if (seen.has(key))
            continue;
        seen.add(key);
        tags.push(label);
    }
    return tags;
}
export function splitMentionParts(text) {
    const parts = [];
    const re = new RegExp(MENTION_TOKEN_RE.source, 'g');
    let last = 0;
    let match;
    while ((match = re.exec(text))) {
        if (match.index > last) {
            parts.push({ type: 'text', value: text.slice(last, match.index) });
        }
        parts.push({ type: 'mention', value: match[0] });
        last = match.index + match[0].length;
    }
    if (last < text.length) {
        parts.push({ type: 'text', value: text.slice(last) });
    }
    return parts;
}
export function filterMentionPeople(people, query) {
    const q = query.trim().toLowerCase();
    return people.filter((person) => {
        const handle = mentionHandle(person, people).toLowerCase();
        const full = [person.firstName, person.lastName].filter(Boolean).join(' ').toLowerCase();
        const email = (person.email ?? '').toLowerCase();
        if (!q)
            return Boolean(handle || full);
        return handle.startsWith(q) || full.includes(q) || email.includes(q);
    });
}
export function filterMentionSuggestions(people, query) {
    const q = query.trim().toLowerCase();
    const suggestions = [];
    const seen = new Set();
    const add = (suggestion) => {
        const key = suggestion.handle.toLowerCase();
        if (!key || seen.has(key))
            return;
        seen.add(key);
        suggestions.push(suggestion);
    };
    for (const tag of UPDATE_MENTION_TAGS) {
        if (!q || tag.toLowerCase().startsWith(q)) {
            add({ kind: 'tag', handle: tag, label: tag });
        }
    }
    if (q && !seen.has(q) && /^[A-Za-z][A-Za-z0-9.'-]*$/.test(query.trim())) {
        add({ kind: 'tag', handle: query.trim(), label: query.trim() });
    }
    for (const person of filterMentionPeople(people, query)) {
        const handle = mentionHandle(person, people);
        const label = [person.firstName, person.lastName].filter(Boolean).join(' ').trim() ||
            person.email ||
            `Person ${person.id}`;
        add({ kind: 'person', handle, label, person });
    }
    return suggestions;
}
/** `@` plus the token being typed at `cursor`, or null when not in a mention. */
export function mentionQueryAtCursor(value, cursor) {
    const before = value.slice(0, cursor);
    const at = before.lastIndexOf('@');
    if (at < 0)
        return null;
    if (at > 0 && /[A-Za-z0-9]/.test(before[at - 1] ?? ''))
        return null;
    const query = before.slice(at + 1);
    if (/[\s\n]/.test(query))
        return null;
    return { start: at, query };
}
